import { PlayerPhoto } from 'common/react/components/PlayerPhoto';
import PropTypes from 'prop-types';
import React from 'react';
import { getTranslation } from 'common/react/utils/translations';
import { useGetImageScale } from 'widgets/team-lineups-v2/js/utils/use-get-image-scale';

/**
 * Player entry for Team Lineups field view
 * Renders player shirt number, name, and conditionally player photo.
 *
 * @param {object} props
 * @param {object} props.player Player object
 * @param {object} props.team Team object
 * @param {boolean} [props.isHomeTeam] Is the team acting as home team in that match
 * @param {boolean} [props.showPhoto] Shoul'd the player photo be displayed
 * @param {boolean} [props.isAFLW] Is the player playing in AFLW. Default FALSE.
 * @param {boolean} [props.className]  Custom css class to add to the element.
 * @param {boolean} [props.trackedView] View in which the PlayerEntry is used. Must be a human readable string. example: "list view"
 *
 * @returns {HTMLDivElement} Player entry element
 */
export const PlayerEntry = ({
    player,
    team,
    isHomeTeam = false,
    showPhoto = false,
    className = ''
}) => {
    const teamAbbr = PULSE.app.common.team.getTeamAbbr(team.teamId);
    const imageScale = useGetImageScale();

    return (
        <p
            className={`team-lineups__player-entry ${className} flex-column ${
                isHomeTeam
                    ? 'team-lineups__player-entry--home-team'
                    : 'team-lineups__player-entry--away-team'
            }`}
            title={`${player?.playerName?.givenName} ${player?.playerName?.surname}`}
            aria-label={`${player?.playerName?.givenName} ${
                player?.playerName?.surname
            }. ${getTranslation(
                `label.playingposition.${player.position.toLowerCase()}`
            )}. ${team.teamName.teamName}.`}
        >
            {showPhoto && (
                <div className="team-lineups__player-entry-photo">
                    <PlayerPhoto
                        className="team-lineups__player-entry-photo--image"
                        photoUrl={player.photoURL}
                        alt={`${player?.playerName?.givenName} ${player?.playerName?.surname}`}
                        scale={imageScale}
                    />
                </div>
            )}
            <div className="team-lineups__player-entry-label flex-row flex-row--nowrap">
                <div
                    className={`team-lineups__player-entry--shirt theme--aflc-${teamAbbr}`}
                >
                    {player?.playerJumperNumber ?? ''}
                </div>
                <div className="team-lineups__player-entry--name">
                    <>
                        <span className="team-lineups__player-entry--name-first">
                            {player?.playerName?.givenName}
                        </span>
                        <span className="team-lineups__player-entry--name-letter">
                            {`${player?.playerName?.givenName?.charAt(0)}.`}
                        </span>
                    </>{' '}
                    <span className="team-lineups__player-entry--name-last">
                        {player?.playerName?.surname}
                    </span>
                </div>
            </div>
        </p>
    );
};

PlayerEntry.propTypes = {
    player: PropTypes.object,
    team: PropTypes.object,
    isHomeTeam: PropTypes.bool,
    showPhoto: PropTypes.bool,
    className: PropTypes.string
};
